import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import type { TelemetryIntegrationEvents } from '@src/telemetry/telemetry-events.types'

import { logSessionStreamEvent } from '../InternalAnalytics'

// Isolates the B2B logo-upload funnel's event handlers from InternalAnalytics.ts's shared
// `events` map, which covers every other event in the app.
export const b2bLogoUploadEvents: Pick<
  TelemetryIntegrationEvents,
  | typeof TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD
  | typeof TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FILE_SELECTED
  | typeof TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_BUTTON_CLICK
  | typeof TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_SHOWN
  | typeof TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_ACTION
  | typeof TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_SUCCESS
  | typeof TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_FAILURE
  | typeof TELEMETRY_EVENT_NAMES.LOGO_CONTENT_CHECK_RESULT
  | typeof TELEMETRY_EVENT_NAMES.LOGO_FAILURE_MODAL_ACTION
  | typeof TELEMETRY_EVENT_NAMES.LOGO_DELETE_PROMPT_SHOWN
  | typeof TELEMETRY_EVENT_NAMES.LOGO_DELETE_ACTION
> = {
  [TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD,
      eventId: properties.eventId,
      context: {
        logoUploaded: properties.logoUploaded,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FILE_SELECTED]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FILE_SELECTED,
      eventId: properties.eventId,
      context: {
        file_type: properties.fileType,
        file_size: properties.fileSize,
        input_method: properties.inputMethod,
        is_replacement: properties.isReplacement,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_BUTTON_CLICK]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_BUTTON_CLICK,
      eventId: properties.eventId,
      context: {
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_SHOWN]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_SHOWN,
      eventId: properties.eventId,
      context: {
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_ACTION]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_BACKGROUND_WARNING_ACTION,
      eventId: properties.eventId,
      context: {
        action: properties.action,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_SUCCESS]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_SUCCESS,
      eventId: properties.eventId,
      context: {
        asset_id: properties.assetId,
        duration_ms: properties.durationMs,
        is_retry: properties.isRetry,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_FAILURE]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_UPLOAD_FMS_FAILURE,
      eventId: properties.eventId,
      context: {
        error_type: properties.errorType,
        error_log: properties.errorLog,
        is_retry: properties.isRetry,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_CONTENT_CHECK_RESULT]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_CONTENT_CHECK_RESULT,
      eventId: properties.eventId,
      // EBB-469: a pending-at-exit result is emitted as the document is tearing down, where the
      // default idle-deferred dispatch never runs. Reuse the EBB-439 keepalive path.
      sendImmediately: properties.rejectReason === 'pending_at_exit',
      context: {
        approved: properties.approved,
        reject_reason: properties.rejectReason,
        reject_code: properties.rejectCode,
        asset_id: properties.assetId,
        check_type: properties.checkType,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_FAILURE_MODAL_ACTION]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_FAILURE_MODAL_ACTION,
      eventId: properties.eventId,
      context: {
        modal_type: properties.modalType,
        action: properties.action,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_DELETE_PROMPT_SHOWN]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_DELETE_PROMPT_SHOWN,
      eventId: properties.eventId,
      context: {
        asset_id: properties.assetId,
        product_id: properties.productId,
      },
    })
  },
  [TELEMETRY_EVENT_NAMES.LOGO_DELETE_ACTION]: properties => {
    logSessionStreamEvent({
      event_type: TELEMETRY_EVENT_NAMES.LOGO_DELETE_ACTION,
      eventId: properties.eventId,
      context: {
        action: properties.action,
        asset_id: properties.assetId,
        product_id: properties.productId,
      },
    })
  },
}
